import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth, usePlatformBrowseMode } from '@/contexts';
import { debug } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_LOGGED_OUT_AUTH_CTA_SHOWN,
  EVENT_LOGGED_OUT_AUTH_CTA_SIGN_UP_CLICKED,
} from '@/lib/analyticsEvents';
import { useLocation, useSearchParams } from 'react-router-dom';
import { isDesktop } from '@/utils/isDesktop';
import { isTablet } from '@/utils/isTablet';

// Delay for popover-to-modal transitions to allow closing animation
const POPOVER_TRANSITION_DELAY = 200;

interface AuthActionOptions {
  title?: string;
  description?: string;
  /** When true, logged-out users may proceed in OPEN browse mode without auth. */
  openBrowseNavigation?: boolean;
}

function shouldBypassAuthInOpenBrowse(
  action: string,
  isOpenBrowse: boolean,
  options?: AuthActionOptions
): boolean {
  if (!isOpenBrowse) return false;
  return options?.openBrowseNavigation === true || action === 'share';
}

/**
 * Custom hook for handling authentication-gated actions.
 * Replaces scattered `if (!user)` checks with a centralized, reusable solution.
 * 
 * @returns Object containing requireAuth function, popover state, and helpers
 * 
 * @example
 * ```tsx
 * const { requireAuth, showPopover, getPopoverProps } = useAuthAction();
 * 
 * const handleSave = () => {
 *   requireAuth('save', onSaveClick);
 * };
 * 
 * {showPopover && <SignUpCTAPopover {...getPopoverProps()} />}
 * ```
 */
export const useAuthAction = () => {
  const { user } = useAuth();
  const { isOpenBrowse } = usePlatformBrowseMode();
  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  /** Path + query for post-auth redirect (e.g. profile location share keeps token & shared_location_id for modal). */
  const pathWithSearchForRedirect = useMemo(() => {
    if (!pathname) return '/';
    const q = searchParams.toString();
    return q ? `${pathname}?${q}` : pathname;
  }, [pathname, searchParams]);
  const [showPopover, setShowPopover] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalRedirectPath, setLoginModalRedirectPath] = useState<string | undefined>(undefined);
  const [actionConfig, setActionConfig] = useState<{
    action: string;
    options?: AuthActionOptions;
  } | null>(null);
  const popoverShownTracked = useRef(false);

  /**
   * Requires authentication before executing an action.
   * If user is not authenticated, shows sign-up popover.
   * If user is authenticated, executes the callback immediately.
   * 
   * @param action - Action identifier (e.g., 'save', 'follow', 'remix', 'search')
   * @param callback - Function to execute if user is authenticated (supports both sync and async)
   * @param options - Optional custom title/description for popover
   */
  const requireAuth = useCallback((
    action: string,
    callback: () => void | Promise<void>,
    options?: AuthActionOptions,
    triggerContext?: Record<string, unknown>
  ) => {
    if (!user) {
      if (shouldBypassAuthInOpenBrowse(action, isOpenBrowse, options)) {
        debug('[useAuthAction] OPEN browse action without auth', { action });
        return callback();
      }
      // Store action info for popover
      debug('[useAuthAction] User not authenticated, showing popover', { action });
      setActionConfig({
        action,
        options
      });
      setShowPopover(true);
      
      // Track auth CTA shown (only once per popover display)
      if (!popoverShownTracked.current) {
        popoverShownTracked.current = true;
        const deviceType = isDesktop() ? 'desktop' : isTablet() ? 'tablet' : 'mobile';
        const pageName = pathname || 'unknown';
        
        WebAnalytics.trackLoggedOutInteraction(
          EVENT_LOGGED_OUT_AUTH_CTA_SHOWN,
          pageName,
          {
            trigger_action: action,
            trigger_context: triggerContext || {},
            device_type: deviceType,
          }
        );
      }
      return;
    }
    // User is authenticated, execute the callback (handles both sync and async)
    debug('[useAuthAction] User authenticated, executing callback', { action });
    const result = callback();
    // If callback returns a promise, log any unhandled rejections but still return the promise for caller handling
    if (result instanceof Promise) {
      result.catch(error => {
        debug('[useAuthAction] Callback promise rejected', { error, action });
      });
    }
    return result;
  }, [user, pathname, isOpenBrowse]);

  /**
   * Gets popover props based on the action type.
   * Provides default messages for common actions.
   */
  const getPopoverProps = useCallback(() => {
    if (!actionConfig) return null;

    // Default messages based on action type
    const messages: Record<string, { title: string; description: string }> = {
      save: {
        title: 'Sign up to save posts',
        description: 'Create an account to save your favorite lists and itineraries for later.'
      },
      follow: {
        title: 'Sign up to follow creators',
        description: 'Create an account to follow your favorite travel creators and see their latest posts and recommendations.'
      },
      remix: {
        title: 'Sign up to remix itineraries',
        description: 'Create an account to remix and customize itineraries from other travelers.'
      },
      search: {
        title: 'Sign up to search cities',
        description: 'Create an account to search for cities and discover travel recommendations.'
      },
      post: {
        title: 'Sign up to create posts',
        description: 'Create an account to share your travel experiences and create itineraries.'
      },
      profile: {
        title: 'Sign up to view profiles',
        description: 'Create an account to view creator profiles and follow your favorite travelers.'
      },
      notifications: {
        title: 'Sign up to view notifications',
        description: 'Create an account to receive notifications about your saved content and followed creators.'
      },
      community: {
        title: 'Sign up to access community',
        description: 'Create an account to access the Globe Thrivers community and connect with other travelers.'
      },
      followers: {
        title: 'Sign up to view followers',
        description: 'Create an account to see who follows this creator and discover other travel enthusiasts.'
      },
      saves: {
        title: 'Sign up to view saves',
        description: 'Create an account to see what content this creator has saved and discover new travel inspiration.'
      },
      remixes: {
        title: 'Sign up to view remixes',
        description: 'Create an account to see remixed versions of this creator\'s itineraries and lists.'
      },
      posts: {
        title: 'Sign up to view posts',
        description: 'Create an account to view all posts and content from this creator.'
      },
      recommendations: {
        title: 'Sign up to view recommendations',
        description: 'Create an account to see all recommendations from this creator and discover amazing places.'
      },
      recs: {
        title: 'Sign up to view recommendations',
        description: 'Create an account to see all recommendations from this creator and discover amazing places.'
      },
      countries: {
        title: 'Sign up to view countries',
        description: 'Create an account to see all countries this creator has visited and explore their travel map.'
      },
      destination: {
        title: 'Sign up to explore destinations',
        description: 'Create an account to discover creator-curated trips and recommendations by destination.'
      },
      travel_style: {
        title: 'Sign up to explore travel styles',
        description: 'Create an account to discover trips that match your travel vibe and preferences.'
      },
      itineraries: {
        title: 'Sign up to view your itineraries',
        description: 'Create an account to access and manage your saved trips and itineraries.'
      },
      explore: {
        title: 'Sign up to explore',
        description:
          'Create an account to browse creators, destinations, travel styles, trending trips, and advisors — all in one place.'
      },
      share: {
        title: 'Sign up to share',
        description: 'Create an account to share places and trips with friends and fellow travelers.'
      },
      place: {
        title: 'Sign up to explore places',
        description: 'Create an account to open full place details, ratings, and more.'
      },
      book: {
        title: 'Sign up to book',
        description: 'Create a free account to view rates and book stays from Globe Thrivers.'
      },
      analytics: {
        title: 'Sign up to view your analytics',
        description:
          'Create an account to see how your trips, lists, and recommendations perform — including views and link activity.'
      }
    };

    const defaultMessage = messages[actionConfig.action] || {
      title: 'Sign up to unlock this feature',
      description: 'Create an account to access all of Globe Thrivers\' features including AI-powered trip planning, creating posts, saving content, and more.'
    };

    return {
      title: actionConfig.options?.title || defaultMessage.title,
      description: actionConfig.options?.description || defaultMessage.description,
      onClose: () => setShowPopover(false)
    };
  }, [actionConfig]);

  const handleSignUpClick = useCallback((redirectPath?: string) => {
    // Preserve query string when callers pass pathname only (e.g. location share ?token=&shared_location_id=)
    const resolvedRedirect =
      redirectPath == null || redirectPath === pathname
        ? pathWithSearchForRedirect
        : redirectPath;

    // Track sign up click
    if (actionConfig) {
      const deviceType = isDesktop() ? 'desktop' : isTablet() ? 'tablet' : 'mobile';
      const pageName = pathname || 'unknown';
      
      WebAnalytics.trackLoggedOutInteraction(
        EVENT_LOGGED_OUT_AUTH_CTA_SIGN_UP_CLICKED,
        pageName,
        {
          trigger_action: actionConfig.action,
          redirect_path: resolvedRedirect,
          device_type: deviceType,
        }
      );
    }
    
    setShowPopover(false);
    setLoginModalRedirectPath(resolvedRedirect);
    // Small delay to allow popover to close before opening LoginModal
    setTimeout(() => {
      setShowLoginModal(true);
    }, POPOVER_TRANSITION_DELAY);
  }, [actionConfig, pathname, pathWithSearchForRedirect]);
  
  // Reset tracking flag when popover closes
  useEffect(() => {
    if (!showPopover) {
      popoverShownTracked.current = false;
    }
  }, [showPopover]);

  return {
    requireAuth,        // Function to wrap actions
    showPopover,       // Boolean for popover visibility
    setShowPopover,    // Function to close popover manually
    getPopoverProps,   // Function to get popover configuration
    showLoginModal,    // Boolean for LoginModal visibility
    setShowLoginModal, // Function to close LoginModal manually
    loginModalRedirectPath, // Redirect path for LoginModal
    handleSignUpClick, // Function to handle sign-up button click (closes popover, opens LoginModal)
    isAuthenticated: !!user  // Helper boolean
  };
};

